import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Camera, Check, Mail, TrendingUp, Pencil, MailPlus, Phone,
  MessageCircle, X, Clock, AlertTriangle, CalendarClock, Users,
  ArrowRight, Sparkles, Zap, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme, ACCENT_KEYS } from '../context/ThemeContext';
import Avatar from './Avatar';
import WeeklySummaryModal from './WeeklySummaryModal';
import CurrencySettings from './CurrencySettings';

const API = process.env.REACT_APP_BACKEND_URL;

// Google logo (multi-color paths). Reused inside "Cuentas vinculadas".
const GoogleIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// WhatsApp logo used for the Wingman card icon.
const WhatsAppIcon = ({ className = 'w-6 h-6' }) => (
  <svg className={className} viewBox="0 0 32 32" fill="currentColor">
    <path d="M16 3C9 3 3.5 8.5 3.5 15.4c0 2.4.6 4.6 1.9 6.6L3 29l7.2-1.9c2 1 4.3 1.6 6.7 1.6h.1c6.9 0 12.5-5.5 12.5-12.4C29.5 8.5 23 3 16 3zm7.2 17.6c-.3.8-1.7 1.6-2.4 1.7-.7.1-1.5.1-2.4-.2-.6-.2-1.3-.4-2.3-.8-4-1.7-6.7-5.8-6.9-6-.2-.3-1.6-2.2-1.6-4.2 0-2 1-2.9 1.4-3.4.4-.4.9-.5 1.2-.5s.6 0 .9 0c.3 0 .7-.1 1.1.8.4.9 1.3 3 1.4 3.2.1.2.2.4 0 .7-.1.3-.2.5-.4.7-.2.2-.4.5-.6.7-.2.2-.4.4-.2.8.2.4 1 1.6 2.1 2.6 1.4 1.3 2.6 1.7 3 1.9.4.2.6.2.8-.1.2-.3.9-1.1 1.2-1.5.3-.4.5-.3.9-.2.4.1 2.4 1.1 2.8 1.3.4.2.7.3.8.5.1.2.1 1-.2 1.7z"/>
  </svg>
);

export default function ProfileView() {
  const { user, setUser, loginWithGoogle, logout } = useAuth();
  const { accent, setAccent } = useTheme();
  const [uploading, setUploading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [linkingPassword, setLinkingPassword] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [emailToggling, setEmailToggling] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(user?.phone || '');
  const [savingPhone, setSavingPhone] = useState(false);
  const [wingman, setWingman] = useState({ configured: false, phone: null, connect_link: null });
  const [orgScore, setOrgScore] = useState(null);
  const [weeklyCounts, setWeeklyCounts] = useState({ upcoming_tasks: 0, overdue_tasks: 0, upcoming_reminders: 0, shared: 0 });
  const fileRef = useRef(null);

  // Wingman config
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/wingman/config`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setWingman(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.phone]);

  // Weekly summary for org score + Resumen Rápido tiles
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/summary/weekly`, { withCredentials: true })
      .then(({ data }) => {
        if (!alive) return;
        const c = data?.counts || {};
        const done = c.completed_this_week || 0;
        const total = done + (c.upcoming_tasks || 0) + (c.overdue_tasks || 0);
        setOrgScore(total > 0 ? Math.round((done / total) * 100) : null);
        setWeeklyCounts({
          upcoming_tasks: c.upcoming_tasks || 0,
          overdue_tasks: c.overdue_tasks || 0,
          upcoming_reminders: c.upcoming_reminders || 0,
          shared: c.shared || 0,
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const providers = user?.auth_providers || (user?.email ? ['email'] : []);
  const hasEmail = providers.includes('email');
  const hasGoogle = providers.includes('google');

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Solo se permiten imágenes JPEG, PNG o WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe pesar menos de 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/api/auth/profile-picture`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUser({ ...user, picture: `${data.picture}?t=${Date.now()}` });
      toast.success('Foto de perfil actualizada');
    } catch { toast.error('Error al subir la foto'); }
    finally { setUploading(false); }
  };

  const handleLinkPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return; }
    setLinkingPassword(true);
    try {
      await axios.post(`${API}/api/auth/link/password`, { password: newPassword }, { withCredentials: true });
      setUser({ ...user, auth_providers: [...new Set([...(user.auth_providers || []), 'email'])] });
      toast.success('Contraseña vinculada correctamente');
      setNewPassword(''); setShowPasswordForm(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al vincular contraseña');
    } finally { setLinkingPassword(false); }
  };

  const handleLinkGoogle = async () => {
    if (!window.confirm('Para vincular Google necesitamos que ingreses con tu cuenta de Google usando el mismo email. Se cerrará tu sesión actual y volverás automáticamente. ¿Continuar?')) return;
    await logout();
    loginWithGoogle();
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    const trimmed = nameDraft.trim();
    if (trimmed.length < 2) { toast.error('El nombre debe tener al menos 2 caracteres'); return; }
    if (trimmed === user?.name) { setEditingName(false); return; }
    setSavingName(true);
    try {
      await axios.patch(`${API}/api/auth/profile`, { name: trimmed }, { withCredentials: true });
      setUser({ ...user, name: trimmed });
      toast.success('Nombre actualizado');
      setEditingName(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al actualizar el nombre');
    } finally { setSavingName(false); }
  };

  const handleToggleEmailSummary = async () => {
    const next = !user?.weekly_summary_email;
    setEmailToggling(true);
    try {
      await axios.patch(`${API}/api/auth/profile`, { weekly_summary_email: next }, { withCredentials: true });
      setUser({ ...user, weekly_summary_email: next });
      toast.success(next ? 'Resumen semanal por email activado' : 'Resumen semanal por email desactivado');
    } catch { toast.error('No pudimos actualizar la preferencia'); }
    finally { setEmailToggling(false); }
  };

  const handleToggleDailyEmail = async () => {
    const next = !user?.daily_summary_email;
    setEmailToggling(true);
    try {
      await axios.patch(`${API}/api/auth/profile`, { daily_summary_email: next }, { withCredentials: true });
      setUser({ ...user, daily_summary_email: next });
      toast.success(next ? 'Resumen diario por email activado' : 'Resumen diario por email desactivado');
    } catch { toast.error('No pudimos actualizar la preferencia'); }
    finally { setEmailToggling(false); }
  };

  const handleSavePhone = async (e) => {
    e.preventDefault();
    const raw = (phoneDraft || '').trim();
    if (raw !== '') {
      const compact = raw.replace(/[\s\-()]/g, '');
      if (!/^\+[1-9]\d{7,14}$/.test(compact)) {
        toast.error('Escribí el teléfono en formato internacional, por ejemplo +5491123456789'); return;
      }
    }
    setSavingPhone(true);
    try {
      const { data } = await axios.patch(`${API}/api/auth/profile`, { phone: raw }, { withCredentials: true });
      setUser({ ...user, phone: data.phone ?? null });
      toast.success(data.phone ? 'Teléfono guardado' : 'Teléfono eliminado');
      setEditingPhone(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al guardar el teléfono');
    } finally { setSavingPhone(false); }
  };

  const memberSince = (() => {
    const iso = user?.created_at;
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const month = d.toLocaleDateString('es-AR', { month: 'long' });
      const year = d.getFullYear();
      return `${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
    } catch { return null; }
  })();

  const orgLabel = orgScore == null ? '—'
    : orgScore >= 85 ? 'Excelente'
    : orgScore >= 65 ? 'Muy bien'
    : orgScore >= 40 ? 'En camino'
    : 'A trabajar';

  const accentDot = {
    orange: 'bg-orange-500', blue: 'bg-blue-600',
    violet: 'bg-violet-500', green: 'bg-emerald-500', pink: 'bg-pink-500',
  };
  const accentName = {
    orange: 'Naranja', blue: 'Azul', violet: 'Violeta', green: 'Verde', pink: 'Rosa',
  };

  return (
    <div data-testid="profile-view" className="space-y-6">
      {/* HEADER — avatar + identidad + Tu organización */}
      <div className="profile-hero p-6 sm:p-8">
        <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
          {/* Avatar */}
          <div className="relative flex-shrink-0 mx-auto md:mx-0">
            <div className="ring-4 ring-[#2563EB]/15 dark:ring-[#3B82F6]/25 rounded-full">
              <Avatar name={user?.name} picture={user?.picture} size="xl" testId="profile-avatar-large" />
            </div>
            <button
              data-testid="change-photo-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute bottom-1 right-1 w-9 h-9 bg-[#2563EB] text-white rounded-full shadow-lg shadow-[#2563EB]/30 hover:brightness-110 transition-all flex items-center justify-center disabled:opacity-50 ring-2 ring-[#F0F4FA] dark:ring-[#0D1730]"
              aria-label="Cambiar foto"
            >
              {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Camera className="w-4 h-4" strokeWidth={2} />}
            </button>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} className="hidden" data-testid="profile-picture-input" />
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0 text-center md:text-left">
            {editingName ? (
              <form onSubmit={handleSaveName} className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <input data-testid="edit-name-input" type="text" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                       className="px-3 py-1.5 text-2xl font-extrabold text-slate-900 dark:text-white bg-slate-100 dark:bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                       autoFocus minLength={2} required />
                <button type="submit" disabled={savingName} data-testid="save-name-btn" className="p-2 bg-emerald-500 text-white rounded-full hover:bg-emerald-600 disabled:opacity-50"><Check className="w-4 h-4" strokeWidth={2.5} /></button>
                <button type="button" onClick={() => { setEditingName(false); setNameDraft(user?.name || ''); }} className="p-2 bg-slate-200 dark:bg-white/10 text-slate-500 dark:text-slate-300 rounded-full hover:bg-slate-300 dark:hover:bg-white/20"><X className="w-4 h-4" strokeWidth={2.5} /></button>
              </form>
            ) : (
              <div className="inline-flex items-center gap-2">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight" data-testid="profile-name">{user?.name}</h1>
                <span title="Cuenta verificada" className="w-5 h-5 rounded-full bg-[#2563EB] text-white flex items-center justify-center flex-shrink-0" aria-label="Cuenta verificada"><Check className="w-3 h-3" strokeWidth={3} /></span>
                <button data-testid="edit-name-btn" onClick={() => { setNameDraft(user?.name || ''); setEditingName(true); }} className="p-1.5 text-slate-400 hover:text-[#2563EB] hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-colors" title="Editar nombre"><Pencil className="w-4 h-4" strokeWidth={2} /></button>
              </div>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1" data-testid="profile-email">{user?.email}</p>
            <div className="mt-3 flex flex-wrap items-center justify-center md:justify-start gap-2">
              {memberSince && <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-[#EEF2FF] dark:bg-[#3B82F6]/10 text-[#2563EB] dark:text-[#60A5FA] ring-1 ring-[#DBEAFE] dark:ring-[#3B82F6]/20"><CalendarClock className="w-3 h-3" strokeWidth={2.5} />Miembro desde {memberSince}</span>}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-200 dark:ring-emerald-500/20"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Cuenta activa</span>
            </div>
          </div>

          {/* Tu organización */}
          <div className="org-tile relative overflow-hidden rounded-2xl px-6 py-5 min-w-[240px] text-white flex-shrink-0">
            <div aria-hidden="true" className="absolute -right-8 -bottom-8 w-40 h-40 rounded-full bg-[#3B82F6]/20 blur-2xl" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">Tu organización</p>
            <p className="text-4xl sm:text-5xl font-extrabold mt-1 leading-none" data-testid="org-score">{orgScore == null ? '—' : `${orgScore}%`}</p>
            <div className="mt-2 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-[#60A5FA]" strokeWidth={2.5} /><p className="text-xs font-semibold text-[#93C5FD]">{orgLabel}</p></div>
            <div className="mt-3 h-1.5 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] rounded-full transition-all" style={{ width: `${orgScore ?? 0}%` }} /></div>
          </div>
        </div>
      </div>

      {/* ROW 1 — Teléfono | Wingman WhatsApp */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teléfono */}
        <div className="premium-card p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#2563EB]/25">
              <Phone className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="section-eyebrow">Teléfono principal</p>
                {!editingPhone && (
                  <button data-testid="edit-phone-btn" onClick={() => { setPhoneDraft(user?.phone || ''); setEditingPhone(true); }} className="p-1.5 text-slate-400 hover:text-[#2563EB] hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-colors" title="Editar teléfono">
                    <Pencil className="w-4 h-4" strokeWidth={2} />
                  </button>
                )}
              </div>
              {editingPhone ? (
                <form onSubmit={handleSavePhone} className="mt-2 flex flex-col sm:flex-row gap-2">
                  <input data-testid="edit-phone-input" type="tel" inputMode="tel" placeholder="+54 9 11 2345-6789" value={phoneDraft} onChange={(e) => setPhoneDraft(e.target.value)}
                         className="flex-1 px-3 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-sm text-slate-900 dark:text-white border border-transparent focus:outline-none focus:ring-2 focus:ring-[#2563EB]" autoFocus />
                  <div className="flex gap-2">
                    <button type="submit" disabled={savingPhone} data-testid="save-phone-btn" className="px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">{savingPhone ? 'Guardando...' : 'Guardar'}</button>
                    <button type="button" onClick={() => { setEditingPhone(false); setPhoneDraft(user?.phone || ''); }} className="px-3 py-2 rounded-xl bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-sm font-semibold">Cancelar</button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="mt-1 text-lg font-bold text-slate-900 dark:text-white truncate" data-testid="profile-phone">{user?.phone || 'Sin número guardado'}</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Usá formato internacional. Ej: +54 9 11 2345-6789</p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Wingman WhatsApp */}
        <div className="premium-card p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-md shadow-emerald-500/25 text-white">
              <WhatsAppIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="section-eyebrow">Wingman en WhatsApp</p>
              <p className="mt-1 text-lg font-bold text-slate-900 dark:text-white">{wingman.configured ? 'Wingman está listo' : 'Aún no está conectado'}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                {wingman.configured ? 'Podés hablarle por WhatsApp cuando quieras.' : 'Conectá WhatsApp para que Wingman te recuerde y organice.'}
              </p>
              {!wingman.configured && wingman.connect_link && (
                <a href={wingman.connect_link} target="_blank" rel="noopener noreferrer" data-testid="connect-whatsapp-btn" className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-md shadow-emerald-500/30 transition-all">
                  <MessageCircle className="w-4 h-4" strokeWidth={2} />
                  Conectar WhatsApp con Wingman
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ROW 2 — Cuentas vinculadas | Resumen rápido */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cuentas vinculadas */}
        <div className="premium-card p-6">
          <p className="section-eyebrow mb-4">Cuentas vinculadas</p>
          <div className="space-y-3">
            {/* Email */}
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-white dark:bg-white/5 ring-1 ring-slate-200/70 dark:ring-white/5">
              <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-slate-500 dark:text-slate-400" strokeWidth={1.8} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Email y contraseña</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
              </div>
              {hasEmail ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"><Check className="w-3 h-3" strokeWidth={2.5} />Vinculado</span>
              ) : (
                <button data-testid="link-password-btn" onClick={() => setShowPasswordForm((s) => !s)} className="text-xs font-semibold text-[#2563EB] hover:underline">Agregar</button>
              )}
            </div>
            {showPasswordForm && !hasEmail && (
              <form onSubmit={handleLinkPassword} className="p-3 bg-white dark:bg-white/5 rounded-2xl space-y-2">
                <input type="password" placeholder="Nueva contraseña (mín 6)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="link-password-input"
                       className="w-full px-3 py-2 rounded-xl bg-white dark:bg-white/5 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#2563EB]" required minLength={6} />
                <button type="submit" disabled={linkingPassword} className="w-full px-3 py-2 rounded-xl bg-[#2563EB] hover:bg-[#1E4FCF] text-white text-sm font-semibold disabled:opacity-50">{linkingPassword ? 'Vinculando...' : 'Vincular contraseña'}</button>
              </form>
            )}
            {/* Google */}
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-white dark:bg-white/5 ring-1 ring-slate-200/70 dark:ring-white/5">
              <div className="w-10 h-10 rounded-xl bg-white ring-1 ring-slate-200 flex items-center justify-center flex-shrink-0"><GoogleIcon className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Google</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Iniciar sesión con Google</p>
              </div>
              {hasGoogle ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"><Check className="w-3 h-3" strokeWidth={2.5} />Vinculado</span>
              ) : (
                <button data-testid="link-google-btn" onClick={handleLinkGoogle} className="text-xs font-semibold text-[#2563EB] hover:underline">Vincular</button>
              )}
            </div>
          </div>
        </div>

        {/* Resumen rápido — 4 tiles */}
        <div className="premium-card p-6">
          <p className="section-eyebrow mb-4">Resumen rápido</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl p-4 bg-[#EEF2FF] dark:bg-[#3B82F6]/10 ring-1 ring-[#DBEAFE] dark:ring-[#3B82F6]/20">
              <div className="flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={2} /><span className="text-[11px] font-semibold uppercase tracking-wider text-[#2563EB] dark:text-[#60A5FA]">Pendientes</span></div>
              <p className="text-3xl font-extrabold text-slate-900 dark:text-white leading-none" data-testid="tile-pendientes">{weeklyCounts.upcoming_tasks}</p>
            </div>
            <div className="rounded-2xl p-4 bg-orange-50 dark:bg-orange-500/10 ring-1 ring-orange-200 dark:ring-orange-500/20">
              <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" strokeWidth={2} /><span className="text-[11px] font-semibold uppercase tracking-wider text-orange-700 dark:text-orange-400">Vencen hoy</span></div>
              <p className="text-3xl font-extrabold text-slate-900 dark:text-white leading-none" data-testid="tile-vencen">{weeklyCounts.overdue_tasks}</p>
            </div>
            <div className="rounded-2xl p-4 bg-[#EEF2FF] dark:bg-[#3B82F6]/10 ring-1 ring-[#DBEAFE] dark:ring-[#3B82F6]/20">
              <div className="flex items-center gap-2 mb-2"><CalendarClock className="w-4 h-4 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={2} /><span className="text-[11px] font-semibold uppercase tracking-wider text-[#2563EB] dark:text-[#60A5FA]">Próximos</span></div>
              <p className="text-3xl font-extrabold text-slate-900 dark:text-white leading-none" data-testid="tile-proximos">{weeklyCounts.upcoming_reminders}</p>
            </div>
            <div className="rounded-2xl p-4 bg-violet-50 dark:bg-violet-500/10 ring-1 ring-violet-200 dark:ring-violet-500/20">
              <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-violet-600 dark:text-violet-400" strokeWidth={2} /><span className="text-[11px] font-semibold uppercase tracking-wider text-violet-700 dark:text-violet-400">Compartidos</span></div>
              <p className="text-3xl font-extrabold text-slate-900 dark:text-white leading-none" data-testid="tile-compartidos">{weeklyCounts.shared}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ROW 3 — Color de la app | Monedas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Color de la app */}
        <div className="premium-card p-6">
          <p className="section-eyebrow mb-4">Color de la app</p>
          <div className="flex items-center gap-3 flex-wrap">
            {ACCENT_KEYS.map((k) => (
              <button key={k} onClick={() => setAccent(k)} data-testid={`accent-${k}`}
                      className={`relative w-11 h-11 rounded-full ${accentDot[k]} flex items-center justify-center transition-transform hover:scale-105 ${accent === k ? 'ring-4 ring-offset-2 ring-offset-[#F0F4FA] dark:ring-offset-[#0B1220] ring-[#2563EB]/40' : 'ring-1 ring-slate-200 dark:ring-white/10'}`}
                      title={accentName[k]}>
                {accent === k && <Check className="w-5 h-5 text-white" strokeWidth={2.5} />}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Se aplica en botones, íconos activos y detalles.</p>
        </div>

        {/* Monedas — reusa el componente existente */}
        <div className="premium-card p-6">
          <p className="section-eyebrow mb-4">Monedas</p>
          <CurrencySettings />
        </div>
      </div>

      {/* ROW 4 — Notificaciones (3 cards) */}
      <div>
        <p className="section-eyebrow mb-3 px-1">Notificaciones</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Resumen semanal por email */}
          <div className="premium-card p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] dark:bg-[#3B82F6]/15 flex items-center justify-center flex-shrink-0"><MailPlus className="w-5 h-5 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={1.8} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Resumen semanal por email</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Recibí un resumen los lunes a la mañana.</p>
              </div>
              <button data-testid="toggle-weekly-email" onClick={handleToggleEmailSummary} disabled={emailToggling} aria-pressed={!!user?.weekly_summary_email}
                      className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${user?.weekly_summary_email ? 'bg-[#2563EB]' : 'bg-slate-300 dark:bg-white/10'} disabled:opacity-60 flex-shrink-0`}>
                <span className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform ${user?.weekly_summary_email ? 'translate-x-5' : 'translate-x-0.5'} translate-y-0.5`} />
              </button>
            </div>
          </div>
          {/* Resumen diario por email */}
          <div className="premium-card p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] dark:bg-[#3B82F6]/15 flex items-center justify-center flex-shrink-0"><Mail className="w-5 h-5 text-[#2563EB] dark:text-[#60A5FA]" strokeWidth={1.8} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Resumen diario por email</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Un pantallazo de lo del día a las 7 am.</p>
              </div>
              <button data-testid="toggle-daily-email" onClick={handleToggleDailyEmail} disabled={emailToggling} aria-pressed={!!user?.daily_summary_email}
                      className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${user?.daily_summary_email ? 'bg-[#2563EB]' : 'bg-slate-300 dark:bg-white/10'} disabled:opacity-60 flex-shrink-0`}>
                <span className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform ${user?.daily_summary_email ? 'translate-x-5' : 'translate-x-0.5'} translate-y-0.5`} />
              </button>
            </div>
          </div>
          {/* Ver mi resumen semanal */}
          <button data-testid="open-weekly-summary" onClick={() => setShowSummary(true)} className="premium-card p-5 text-left hover:brightness-105 dark:hover:brightness-125 transition-all group">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#2563EB]/25"><Eye className="w-5 h-5 text-white" strokeWidth={1.8} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Mi resumen semanal</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Todo lo que hiciste y lo que viene.</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#2563EB] flex-shrink-0 group-hover:translate-x-0.5 transition-all" strokeWidth={2} />
            </div>
          </button>
        </div>
      </div>

      {/* NEO FOOTER — barra premium full-width */}
      <div className="relative overflow-hidden rounded-3xl px-6 sm:px-10 py-6 sm:py-8 bg-gradient-to-br from-[#0F172A] via-[#0B1220] to-[#1E3A8A] dark:from-[#050912] dark:via-[#0a0f1c] dark:to-[#1E3A8A] text-white ring-1 ring-white/5">
        <div aria-hidden="true" className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-[#3B82F6]/20 blur-3xl" />
        <div aria-hidden="true" className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full bg-[#2563EB]/20 blur-3xl" />
        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center flex-shrink-0 ring-2 ring-white/10 shadow-xl shadow-[#3B82F6]/40">
              <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
                <circle cx="9" cy="11" r="1.3" fill="currentColor" />
                <circle cx="15" cy="11" r="1.3" fill="currentColor" />
                <path d="M9 15c1 1 2 1.5 3 1.5s2-.5 3-1.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <p className="text-lg font-extrabold leading-none">NEO</p>
              <p className="text-xs text-white/60 mt-1">Tu asistente inteligente</p>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 md:pl-6 md:border-l md:border-white/10">
            <div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-[#60A5FA] mt-0.5 flex-shrink-0" strokeWidth={2} /><p className="text-xs text-white/80">Te avisa lo importante.</p></div>
            <div className="flex items-start gap-2"><Zap className="w-4 h-4 text-[#60A5FA] mt-0.5 flex-shrink-0" strokeWidth={2} /><p className="text-xs text-white/80">Te ahorra tiempo cada día.</p></div>
            <div className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#60A5FA] mt-0.5 flex-shrink-0" strokeWidth={2} /><p className="text-xs text-white/80">Te da claridad y control.</p></div>
          </div>
          <a href={wingman.connect_link || '#'} target={wingman.connect_link ? '_blank' : undefined} rel="noopener noreferrer" data-testid="neo-cta"
             className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0F172A] font-semibold text-sm shadow-lg hover:brightness-95 transition-all flex-shrink-0">
            <MessageCircle className="w-4 h-4" strokeWidth={2} /> Hablar con Neo
          </a>
        </div>
      </div>

      {showSummary && <WeeklySummaryModal onClose={() => setShowSummary(false)} userName={user?.name} />}
    </div>
  );
}
